// src/components/ZodiacStonesPage.tsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";

import Aquarius from "../assests/Aquarius.webp";
import Aries from "../assests/Aries.webp";
import Cancer from "../assests/Cancer.webp";
import Capricorn from "../assests/Capricorn.webp";
import Gemini from "../assests/Gemini.webp";
import Leo from "../assests/Leo.webp";
import Libra from "../assests/Libra.webp";
import Pisces from "../assests/Pisces.webp";
import Sagittarius from "../assests/Sagittarius.webp";
import Scorpio from "../assests/Scorpio.webp";
import Taurus from "../assests/Taurus.webp";
import Virgo from "../assests/Virgo.webp";

type ZodiacStone = {
  id: string;
  monthLabel: string;
  zodiac: string;
  range: string;
  image: string;
};

const ZODIAC_STONES: ZodiacStone[] = [
  {
    id: "capricorn",
    monthLabel: "DEC 15",
    zodiac: "Capricorn",
    range: "15 Dec – 14 Jan",
    image: Capricorn,
  },
  {
    id: "aquarius",
    monthLabel: "JAN 15",
    zodiac: "Aquarius",
    range: "15 Jan – 15 Feb",
    image: Aquarius,
  },
  {
    id: "pisces",
    monthLabel: "FEB 16",
    zodiac: "Pisces",
    range: "16 Feb – 15 Mar",
    image: Pisces,
  },
  {
    id: "aries",
    monthLabel: "MAR 16",
    zodiac: "Aries",
    range: "16 Mar – 15 Apr",
    image: Aries,
  },
  {
    id: "taurus",
    monthLabel: "APR 16",
    zodiac: "Taurus",
    range: "16 Apr – 15 May",
    image: Taurus,
  },
  {
    id: "gemini",
    monthLabel: "MAY 16",
    zodiac: "Gemini",
    range: "16 May – 15 Jun",
    image: Gemini,
  },
  {
    id: "cancer",
    monthLabel: "JUN 16",
    zodiac: "Cancer",
    range: "16 Jun – 15 Jul",
    image: Cancer,
  },
  {
    id: "leo",
    monthLabel: "JUL 16",
    zodiac: "Leo",
    range: "16 Jul – 15 Aug",
    image: Leo,
  },
  {
    id: "virgo",
    monthLabel: "AUG 16",
    zodiac: "Virgo",
    range: "16 Aug – 15 Sep",
    image: Virgo,
  },
  {
    id: "libra",
    monthLabel: "SEP 16",
    zodiac: "Libra",
    range: "16 Sep – 15 Oct",
    image: Libra,
  },
  {
    id: "scorpio",
    monthLabel: "OCT 16",
    zodiac: "Scorpio",
    range: "16 Oct – 15 Nov",
    image: Scorpio,
  },
  {
    id: "sagittarius",
    monthLabel: "NOV 16",
    zodiac: "Sagittarius",
    range: "16 Nov – 14 Dec",
    image: Sagittarius,
  },
];

const ZodiacStonesPage: React.FC = () => {
  return (
    <Box sx={{ bgcolor: "#F5F5F5", py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography
            sx={{
              fontFamily: `"Bodoni Moda", serif`,
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 600,
              letterSpacing: ".12em",
              textTransform: "uppercase",
            }}
          >
            Choose Your Stone
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: { xs: ".95rem", md: "1.05rem" },
              color: "text.secondary",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Choose your stone according to your zodiac sign and discover the gem
            that was made for you.
          </Typography>
        </Box>

        {/* Stones grid */}
        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {ZODIAC_STONES.map((stone) => (
            <Grid key={stone.id} item xs={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#FCFCFC",
                  border: "1px solid rgba(0,0,0,0.06)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardMedia
                  component="img"
                  loading="lazy"
                  decoding="async"
                  image={stone.image}
                  alt={stone.zodiac}
                  sx={{
                    aspectRatio: "4/3",
                    objectFit: "cover",
                  }}
                />
                <CardContent
                  sx={{
                    textAlign: "center",
                    px: 2.5,
                    pt: 2.5,
                    pb: 2.8,
                  }}
                >
                  {/* line 1: month short label */}
                  <Typography
                    sx={{
                      fontFamily: `"Bodoni Moda", serif`,
                      fontSize: 14,
                      letterSpacing: ".16em",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    {stone.monthLabel}
                  </Typography>

                  {/* line 2: zodiac name */}
                  <Typography
                    sx={{
                      fontFamily: `"Bodoni Moda", serif`,
                      fontWeight: 600,
                      fontSize: 18,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    {stone.zodiac}
                  </Typography>

                  {/* line 3: date range */}
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "text.secondary",
                    }}
                  >
                    {stone.range}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ZodiacStonesPage;
